/**
 * What a postcard needs before it can be printed.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * PCM HOLDS THE DESIGN. THIS HOLDS WHICH DESIGN, AND WHETHER IT CAN SHIP.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The artwork, the copy and the layout live in PCM. What has to be answerable
 * from our own records months later is narrower and does not change: which
 * design each touch used, what its QR pointed at, what size and class it went
 * as, and whether anybody approved it. Every response number depends on those
 * five things and on nothing else the design tool holds.
 *
 * `pcm_design_id` HAD NO WRITER AT ALL before this screen. Migration 047 added
 * it and creative_for_send.use_pcm_design reads it, so send-drop would take
 * the PCM path the moment it was set -- and nothing set it. The whole design
 * linkage was a column set nobody could reach.
 */

import type { DataProvider } from "ra-core";

/** The five PCM sizes, as `creatives_pcm_size_check` spells them. */
export const PCM_SIZES = [
  { key: "46S", label: "4×6 small" },
  { key: "46", label: "4×6" },
  { key: "68", label: "6×8" },
  { key: "69", label: "6×9" },
  { key: "611", label: "6×11" },
] as const;

/** Only these two go on a postcard; `creatives_mail_class_check`. */
export const MAIL_CLASSES = ["FirstClass", "Standard"] as const;

/** The sizes USPS will not carry at Standard; `creatives_small_is_firstclass`. */
export const FIRST_CLASS_ONLY = ["46S", "46"] as const;

/**
 * WHAT A PERSON CAN ACTUALLY DECIDE.
 *
 * Size and the QR destination are NOT here, and that is the point. PCM states
 * both -- `Design.size.key` is "Internal size key" in their own spec, and
 * `GET /qr-code/{id}.url` is "URL this QR Code points to". Offering a box for
 * either meant two places held one fact and only one of them was printed.
 * They are synced, and shown as stated values.
 */
export type CreativeDraft = {
  name: string;
  status: string;
  offerUrl: string;
  pcmDesignId: string;
  mailClass: string;
  notes: string;
};

export type CreativePatch = {
  name: string;
  status: string;
  offer_url: string | null;
  pcm_design_id: number | null;
  mail_class: string | null;
  notes: string | null;
};

export type CreativeCheck = {
  /** Every problem, not the first, so the card is fixed in one pass. */
  errors: string[];
  patch: CreativePatch | null;
  changed: boolean;
};

const blankToNull = (v: string) => (v.trim() === "" ? null : v.trim());

/**
 * The stored row, as far as this check reads it.
 *
 * Wider than CreativePatch on purpose: `pcm_size_key` and
 * `artwork_front_path` are read here and never written by this screen, so
 * typing `current` as the patch would have hidden exactly the fields that
 * decide two of the rules below.
 */
export type CreativeContext = Partial<CreativePatch> & {
  pcm_size_key?: string | null;
  artwork_front_path?: string | null;
};

export function checkCreative(
  draft: CreativeDraft,
  current: CreativeContext,
): CreativeCheck {
  const errors: string[] = [];

  const name = draft.name.trim();
  if (name === "")
    errors.push("A card needs a name — it is how a drop is read back.");

  /*
    URLs ARE CHECKED HERE BECAUSE THE DATABASE CHECKS THEM TOO.
    creatives_offer_url_check and creatives_qr_target_check both demand
    ^https?://. Catching it here means a typo is a sentence rather than a
    constraint name in a toast.
  */
  for (const [label, raw] of [["The printed URL", draft.offerUrl]] as const) {
    const v = raw.trim();
    if (v !== "" && !/^https?:\/\//i.test(v))
      errors.push(`${label} must start with http:// or https://.`);
  }

  let designId: number | null = null;
  const rawId = draft.pcmDesignId.trim();
  if (rawId !== "") {
    if (!/^\d+$/.test(rawId))
      errors.push(
        `“${rawId}” is not a PCM design id — they are whole numbers.`,
      );
    else designId = Number(rawId);
  }

  // The size is whatever PCM last said it was. It is read here, never typed.
  const size = current.pcm_size_key ?? null;
  const mailClass = blankToNull(draft.mailClass);

  if (mailClass !== null && !MAIL_CLASSES.includes(mailClass as never))
    errors.push(`“${mailClass}” is not a mail class.`);

  /*
    THE SMALL CARDS CANNOT GO STANDARD, and the database refuses it:
    creatives_small_is_firstclass. Saying so here means the reason arrives
    with the choice rather than after it -- and the reason is a postal rule
    nobody should have to remember while picking a size.
  */
  if (
    size !== null &&
    (FIRST_CLASS_ONLY as readonly string[]).includes(size) &&
    mailClass === "Standard"
  )
    errors.push(
      `A ${PCM_SIZES.find((s) => s.key === size)?.label} card cannot go ` +
        `Standard — USPS will not carry it at that rate. The database ` +
        `refuses it too: creatives_small_is_firstclass.`,
    );

  /*
    APPROVING A CARD WITH NOTHING TO PRINT is the one state that looks
    finished and is not. `printable` in creative_readiness asks for a design
    id or both uploaded sides; a card approved with neither reports as ready
    to nobody and is the shape of thing that gets discovered at the printer.
  */
  const hasSomethingToPrint =
    designId !== null ||
    (current.pcm_design_id ?? null) !== null ||
    Boolean(current.artwork_front_path);
  if (draft.status === "approved" && !hasSomethingToPrint)
    errors.push(
      "Approving a card with no PCM design and no artwork marks it ready " +
        "when nothing could be printed. Link the design first.",
    );

  const patch: CreativePatch = {
    name,
    status: draft.status,
    offer_url: blankToNull(draft.offerUrl),
    pcm_design_id: designId,
    mail_class: mailClass,
    notes: blankToNull(draft.notes),
  };

  const changed = (Object.keys(patch) as (keyof CreativePatch)[]).some(
    (k) => (current[k] ?? null) !== patch[k],
  );

  return { errors, patch: errors.length ? null : patch, changed };
}

/**
 * Save the card, and stamp the approval when it is first approved.
 *
 * `approved_at` IS DERIVED, NOT TYPED. A date somebody enters is a date
 * somebody can get wrong, and the only question it answers -- "when did this
 * become sendable" -- has exactly one correct answer the moment status
 * changes. It is cleared again if a card goes back to draft, because an
 * approval date on an unapproved card is a claim nothing supports.
 */
export async function saveCreative(
  dataProvider: DataProvider,
  id: number | null,
  touch: string,
  patch: CreativePatch,
  wasApproved: boolean,
): Promise<void> {
  const nowApproved = patch.status === "approved";
  const stamp =
    nowApproved === wasApproved
      ? {}
      : { approved_at: nowApproved ? new Date().toISOString() : null };

  if (id == null)
    await dataProvider.create("creatives", {
      data: { touch, ...patch, ...stamp },
    });
  else
    await dataProvider.update("creatives", {
      id,
      data: { ...patch, ...stamp },
      previousData: { id },
    });
}

/* ── adding and removing a touch ───────────────────────────────────────── */

/**
 * The next free touch number.
 *
 * WHY THE BOARD IS NOT A CONSTANT. It used to render `T01`…`T06` from
 * Array.from({length: 6}), which meant the number six lived in one line of
 * TSX and nowhere else — not in a table, not in a constraint. A seventh touch
 * inserted in the database simply did not appear, and there was no way to add
 * or remove one at all. `creatives_touch_check` allows ^T[0-9]{2}$, so the
 * real ceiling is T99.
 *
 * IT FILLS GAPS. Deleting T03 and adding one should give T03 back rather than
 * T07: the numbers are positions in a sequence a person reads, and a board
 * that goes T01 T02 T04 T07 is a board somebody has to explain.
 */
export function nextTouch(existing: readonly string[]): string | null {
  const taken = new Set(existing);
  for (let n = 1; n <= 99; n++) {
    const t = `T${String(n).padStart(2, "0")}`;
    if (!taken.has(t)) return t;
  }
  // 99 touches is not a campaign, and the column could not hold a hundredth.
  return null;
}

/** What a touch is referenced by elsewhere, for the removal check. */
export type TouchUsage = {
  /** Rows in `mailings` — something was actually sent for this touch. */
  mailings: number;
  /** Rows in `drop_schedule` — a cell is planned to receive it. */
  scheduled: number;
};

/**
 * May this touch be removed?
 *
 * NOTHING IN THE DATABASE STOPS THIS, and that is exactly why the check is
 * here. `mailings.touch` and `drop_schedule.touch` are plain text with no
 * foreign key to `creatives` — deleting a creative would leave those rows
 * pointing at a touch that has no card, and the answer to "what did we send
 * for T03" would be gone while the record of sending it remained.
 *
 * A SCHEDULE IS A WARNING; A MAILING IS A REFUSAL. Un-scheduling is somebody
 * changing their mind, which is ordinary. Something already in the post is
 * history, and history does not get tidied away to make a board shorter.
 */
export function touchRemovalVerdict(
  touch: string,
  usage: TouchUsage,
): { ok: true } | { ok: false; reason: string } {
  if (usage.mailings > 0)
    return {
      ok: false,
      reason:
        `${usage.mailings} ${usage.mailings === 1 ? "piece" : "pieces"} of mail ` +
        `went out as ${touch}. Removing the card would leave that history ` +
        `pointing at a touch nothing describes.`,
    };

  if (usage.scheduled > 0)
    return {
      ok: false,
      reason:
        `${usage.scheduled} ${usage.scheduled === 1 ? "cell is" : "cells are"} ` +
        `scheduled to receive ${touch}. Unschedule it on the Drops board first.`,
    };

  return { ok: true };
}
